import React, { useState, useEffect, useCallback } from "react";
import {
  Grid, Card, CardMedia, CardContent, CardActions,
  Button, Typography, Chip, CircularProgress,
  Tabs, Tab, TextField, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import CloseIcon from "@material-ui/icons/Close";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import { toast } from "react-toastify";

import { listMarketplace } from "../../services/appMarketplaceService";
import { getBackendUrl } from "../../config";

const backendUrl = getBackendUrl();

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(4),
    width: "100%",
    background: "#f8fafc",
    minHeight: "100vh",
  },
  pageHeader: { marginBottom: theme.spacing(3) },
  pageTitle: {
    fontSize: "28px",
    fontWeight: 700,
    color: "#1a1a2e",
    marginBottom: theme.spacing(0.5),
  },
  pageSubtitle: { fontSize: "14px", color: "#6b7280" },
  tabsRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "#ffffff",
    borderRadius: 12,
    padding: theme.spacing(0.5, 1),
    marginBottom: theme.spacing(3),
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
    flexWrap: "wrap",
    gap: 8,
  },
  tabs: {
    "& .MuiTabs-indicator": {
      backgroundColor: theme.palette.primary.main,
      height: 3,
      borderRadius: 2,
    },
    flex: 1,
  },
  tab: {
    textTransform: "none",
    fontWeight: 500,
    fontSize: "14px",
    minHeight: 48,
    color: "#6b7280",
    "&.Mui-selected": { color: theme.palette.primary.main, fontWeight: 600 },
  },
  searchField: {
    backgroundColor: "#f8fafc",
    borderRadius: 8,
    minWidth: 220,
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      "& fieldset": { borderColor: "#e0e0e0" },
      "&:hover fieldset": { borderColor: theme.palette.primary.main },
    },
  },
  content: {
    background: "#ffffff",
    borderRadius: 12,
    padding: theme.spacing(3),
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
  },
  card: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    borderRadius: 12,
    border: "1px solid #e2e8f0",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    transition: "box-shadow 0.2s, transform 0.15s",
    cursor: "pointer",
    "&:hover": {
      boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
      transform: "translateY(-3px)",
    },
  },
  cardMedia: { height: 160, backgroundSize: "cover", backgroundPosition: "center" },
  cardMediaPlaceholder: {
    height: 160,
    background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.dark} 100%)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  placeholderInitials: { color: "#fff", fontWeight: 800, fontSize: 32, opacity: 0.9 },
  cardContent: { flex: 1, paddingBottom: 0 },
  categoryChip: { fontSize: "0.7rem", height: 20, marginBottom: 8 },
  cardTitle: { fontWeight: 700, fontSize: "0.95rem", marginBottom: 6, color: "#1e293b" },
  shortDesc: {
    color: "#64748b",
    fontSize: "0.82rem",
    lineHeight: 1.5,
    display: "-webkit-box",
    "-webkit-line-clamp": 3,
    "-webkit-box-orient": "vertical",
    overflow: "hidden",
  },
  cardActions: { justifyContent: "flex-end", padding: "8px 16px 16px" },
  emptyState: { textAlign: "center", padding: "48px 0", color: "#94a3b8" },
  loadingBox: { display: "flex", justifyContent: "center", padding: "48px 0" },
  // Detail dialog
  detailCover: {
    width: "100%",
    maxHeight: 300,
    objectFit: "cover",
    borderRadius: 10,
    marginBottom: 16,
    boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
  },
  detailCoverPlaceholder: {
    width: "100%",
    height: 200,
    borderRadius: 10,
    marginBottom: 16,
    background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.dark} 100%)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  detailSectionTitle: {
    fontWeight: 700,
    fontSize: "0.9rem",
    color: "#1e293b",
    marginBottom: 8,
    marginTop: 20,
    paddingBottom: 4,
    borderBottom: "2px solid #f1f5f9",
  },
  galleryGrid: { display: "flex", flexWrap: "wrap", gap: 8 },
  galleryThumb: {
    width: 110,
    height: 80,
    objectFit: "cover",
    borderRadius: 8,
    cursor: "pointer",
    border: "2px solid transparent",
    transition: "border-color 0.15s, transform 0.15s",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      transform: "scale(1.05)",
    },
  },
  galleryThumbActive: {
    border: `2px solid ${theme.palette.primary.main}`,
  },
  videoWrapper: {
    position: "relative",
    paddingTop: "56.25%",
    borderRadius: 8,
    overflow: "hidden",
    background: "#000",
  },
  videoIframe: {
    position: "absolute",
    top: 0, left: 0,
    width: "100%", height: "100%",
    border: "none",
  },
  descriptionHtml: {
    fontSize: "0.9rem",
    lineHeight: 1.75,
    color: "#334155",
    "& p": { marginBottom: 8 },
    "& ul, & ol": { paddingLeft: 20, marginBottom: 8 },
    "& h1, & h2, & h3": { marginBottom: 6, color: "#1e293b", fontWeight: 700 },
    "& strong": { color: "#1e293b" },
    "& li": { marginBottom: 3 },
  },
  // Lightbox
  lightboxBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.92)",
    zIndex: 1500,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  lightboxImg: {
    maxWidth: "90vw",
    maxHeight: "88vh",
    objectFit: "contain",
    borderRadius: 6,
    userSelect: "none",
  },
  lightboxClose: {
    position: "absolute",
    top: 16,
    right: 16,
    color: "#fff",
    background: "rgba(255,255,255,0.12)",
    "&:hover": { background: "rgba(255,255,255,0.25)" },
  },
  lightboxNav: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#fff",
    background: "rgba(255,255,255,0.12)",
    "&:hover": { background: "rgba(255,255,255,0.25)" },
    "&:disabled": { opacity: 0.2 },
  },
  lightboxNavLeft: { left: 16 },
  lightboxNavRight: { right: 16 },
  lightboxCounter: {
    position: "absolute",
    bottom: 20,
    left: "50%",
    transform: "translateX(-50%)",
    color: "#fff",
    fontSize: "0.85rem",
    background: "rgba(0,0,0,0.5)",
    padding: "4px 12px",
    borderRadius: 20,
  },
}));

const getYoutubeEmbedUrl = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url);
    let vid = u.searchParams.get("v");
    if (!vid && u.hostname === "youtu.be") vid = u.pathname.slice(1);
    if (!vid && u.pathname.includes("/shorts/")) vid = u.pathname.split("/shorts/")[1];
    if (vid) return `https://www.youtube.com/embed/${vid}`;
  } catch {}
  return null;
};

const AppMarketplace = () => {
  const classes = useStyles();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Todas");
  const [detailItem, setDetailItem] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    listMarketplace()
      .then(({ data }) => setItems(data))
      .catch(() => toast.error("Erro ao carregar marketplace"))
      .finally(() => setLoading(false));
  }, []);

  const gallery = (detailItem && detailItem.gallery) || [];

  const openLightbox = (index) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);

  const lightboxPrev = useCallback(() => {
    setLightboxIndex((i) => (i > 0 ? i - 1 : gallery.length - 1));
  }, [gallery.length]);

  const lightboxNext = useCallback(() => {
    setLightboxIndex((i) => (i < gallery.length - 1 ? i + 1 : 0));
  }, [gallery.length]);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const handler = (e) => {
      if (e.key === "ArrowLeft") lightboxPrev();
      if (e.key === "ArrowRight") lightboxNext();
      if (e.key === "Escape") closeLightbox();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightboxIndex, lightboxPrev, lightboxNext]);

  const usedCategories = ["Todas", ...new Set(items.map((i) => i.category).filter(Boolean))];

  const filtered = items.filter((i) => {
    const matchCat = selectedCategory === "Todas" || i.category === selectedCategory;
    const matchSearch = !search.trim() ||
      i.title.toLowerCase().includes(search.toLowerCase()) ||
      (i.shortDescription || "").toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const embedUrl = detailItem ? getYoutubeEmbedUrl(detailItem.videoUrl) : null;

  return (
    <div className={classes.root}>
      {/* Lightbox */}
      {lightboxIndex !== null && gallery.length > 0 && (
        <div className={classes.lightboxBackdrop} onClick={closeLightbox}>
          <IconButton
            className={`${classes.lightboxNav} ${classes.lightboxNavLeft}`}
            onClick={(e) => { e.stopPropagation(); lightboxPrev(); }}
          >
            <ArrowBackIosIcon />
          </IconButton>

          <img
            src={`${backendUrl}/public/${gallery[lightboxIndex]}`}
            alt={`Galeria ${lightboxIndex + 1}`}
            className={classes.lightboxImg}
            onClick={(e) => e.stopPropagation()}
          />

          <IconButton
            className={`${classes.lightboxNav} ${classes.lightboxNavRight}`}
            onClick={(e) => { e.stopPropagation(); lightboxNext(); }}
          >
            <ArrowForwardIosIcon />
          </IconButton>

          <IconButton className={classes.lightboxClose} onClick={closeLightbox}>
            <CloseIcon />
          </IconButton>

          <span className={classes.lightboxCounter}>
            {lightboxIndex + 1} / {gallery.length}
          </span>
        </div>
      )}

      {/* Dialog de detalhe */}
      <Dialog
        open={Boolean(detailItem)}
        onClose={() => setDetailItem(null)}
        fullWidth
        maxWidth="md"
        scroll="paper"
      >
        {detailItem && (
          <>
            <DialogTitle>
              {detailItem.title}
              <IconButton
                size="small"
                style={{ position: "absolute", right: 12, top: 12 }}
                onClick={() => setDetailItem(null)}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              {detailItem.coverImage ? (
                <img
                  src={`${backendUrl}/public/${detailItem.coverImage}`}
                  alt={detailItem.title}
                  className={classes.detailCover}
                />
              ) : (
                <div className={classes.detailCoverPlaceholder}>
                  <Typography style={{ color: "#fff", fontWeight: 800, fontSize: 48, opacity: 0.85 }}>
                    {detailItem.title.substring(0, 2).toUpperCase()}
                  </Typography>
                </div>
              )}

              {detailItem.category && (
                <Chip label={detailItem.category} size="small" color="primary" style={{ marginBottom: 12 }} />
              )}

              {detailItem.shortDescription && (
                <Typography style={{ color: "#475569", fontSize: "1rem", marginBottom: 12, lineHeight: 1.6 }}>
                  {detailItem.shortDescription}
                </Typography>
              )}

              {detailItem.description && (
                <>
                  <Typography className={classes.detailSectionTitle}>Sobre</Typography>
                  <div
                    className={classes.descriptionHtml}
                    dangerouslySetInnerHTML={{ __html: detailItem.description }}
                  />
                </>
              )}

              {gallery.length > 0 && (
                <>
                  <Typography className={classes.detailSectionTitle}>Galeria</Typography>
                  <div className={classes.galleryGrid}>
                    {gallery.map((img, idx) => (
                      <img
                        key={img}
                        src={`${backendUrl}/public/${img}`}
                        alt={`Galeria ${idx + 1}`}
                        className={`${classes.galleryThumb} ${lightboxIndex === idx ? classes.galleryThumbActive : ""}`}
                        onClick={() => openLightbox(idx)}
                      />
                    ))}
                  </div>
                </>
              )}

              {embedUrl && (
                <>
                  <Typography className={classes.detailSectionTitle}>Vídeo</Typography>
                  <div className={classes.videoWrapper}>
                    <iframe
                      src={embedUrl}
                      title={detailItem.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className={classes.videoIframe}
                    />
                  </div>
                </>
              )}

              {detailItem.videoFile && !embedUrl && (
                <>
                  <Typography className={classes.detailSectionTitle}>Vídeo</Typography>
                  <video
                    src={`${backendUrl}/public/${detailItem.videoFile}`}
                    controls
                    style={{ width: "100%", borderRadius: 8 }}
                  />
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailItem(null)}>Fechar</Button>
              {detailItem.buttonUrl && (
                <Button
                  variant="contained"
                  color="primary"
                  endIcon={<OpenInNewIcon />}
                  onClick={() => window.open(detailItem.buttonUrl, "_blank")}
                >
                  {detailItem.buttonText || "Saiba Mais"}
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Header */}
      <div className={classes.pageHeader}>
        <Typography className={classes.pageTitle}>App Marketplace</Typography>
        <Typography className={classes.pageSubtitle}>
          Explore aplicativos e integrações disponíveis para o seu sistema
        </Typography>
      </div>

      {/* Abas + busca */}
      {!loading && items.length > 0 && (
        <div className={classes.tabsRow}>
          <Tabs
            value={selectedCategory}
            onChange={(_, v) => setSelectedCategory(v)}
            className={classes.tabs}
            variant="scrollable"
            scrollButtons="auto"
          >
            {usedCategories.map((cat) => (
              <Tab key={cat} label={cat} value={cat} className={classes.tab} />
            ))}
          </Tabs>
          <TextField
            placeholder="Buscar apps..."
            variant="outlined"
            size="small"
            className={classes.searchField}
            value={search}
            onChange={(e) => { const v = e.target.value; setSearch(v); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "#999", fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />
        </div>
      )}

      {/* Cards */}
      <div className={classes.content}>
        {loading ? (
          <div className={classes.loadingBox}>
            <CircularProgress />
          </div>
        ) : filtered.length === 0 ? (
          <div className={classes.emptyState}>
            <Typography variant="h6" style={{ color: "#64748b" }}>
              Nenhum app encontrado
            </Typography>
            <Typography variant="body2" style={{ marginTop: 8 }}>
              {search || selectedCategory !== "Todas"
                ? "Tente outros termos de busca ou outra categoria."
                : "Em breve novos aplicativos e integrações serão adicionados."}
            </Typography>
          </div>
        ) : (
          <Grid container spacing={3}>
            {filtered.map((item) => (
              <Grid key={item.id} item xs={12} sm={6} md={4} lg={3}>
                <Card className={classes.card} onClick={() => setDetailItem(item)}>
                  {item.coverImage ? (
                    <CardMedia
                      className={classes.cardMedia}
                      image={`${backendUrl}/public/${item.coverImage}`}
                      title={item.title}
                    />
                  ) : (
                    <div className={classes.cardMediaPlaceholder}>
                      <Typography className={classes.placeholderInitials}>
                        {item.title.substring(0, 2).toUpperCase()}
                      </Typography>
                    </div>
                  )}
                  <CardContent className={classes.cardContent}>
                    {item.category && (
                      <Chip
                        label={item.category}
                        size="small"
                        color="primary"
                        className={classes.categoryChip}
                      />
                    )}
                    <Typography className={classes.cardTitle}>{item.title}</Typography>
                    {item.shortDescription && (
                      <Typography className={classes.shortDesc}>
                        {item.shortDescription}
                      </Typography>
                    )}
                  </CardContent>
                  <CardActions className={classes.cardActions}>
                    <Button
                      size="small"
                      color="primary"
                      variant="outlined"
                      style={{ textTransform: "none", borderRadius: 8, fontWeight: 600 }}
                      onClick={(e) => { e.stopPropagation(); setDetailItem(item); }}
                    >
                      {item.buttonText || "Saiba Mais"}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </div>
    </div>
  );
};

export default AppMarketplace;
